import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DropZone from "./DropZone";
import Modal from "./Modal";
import LayoutModal from "./LayoutModal";
import defaultContent from "../../content/defaultContent.json";
import { normalizeContent } from "../../content/normalizeContent";
import { layoutWorkDetail, workDetailPageHeightVh } from "../../lib/workDetailLayout";
import { layoutWorksGallery, pageHeightVh } from "../../lib/worksLayout";

// Single-page editor. Loads /api/content (KV current); falls back to the
// bundled default if KV is empty. PUT /api/admin/content on save.
//
// Sticky save bar at the bottom shows version + last-updated and a Save
// button that disables itself if local state matches the loaded doc.
//
// Lifecycle nuances:
// - On mount, if `rf_admin_pendingDoc` is set in sessionStorage we use it
//   as the working doc (recovers an in-progress edit that hit a 401 and
//   forced a re-login). The key is consumed on read.
// - On save 401, we stash the current doc as pendingDoc AND the path as
//   returnTo, so re-login bounces back here AND restores the edits.
// - The footer's `last saved` reflects the server response after a save,
//   not the value the doc happened to be loaded with.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AdminEdit() {
  const navigate = useNavigate();
  const location = useLocation();
  const [doc, setDoc] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [tileLayoutOpen, setTileLayoutOpen] = useState(false);

  useEffect(() => {
    document.title = "Edit · Ruby Fos";
    let alive = true;

    // Rescue an in-progress edit that hit a 401 mid-save.
    let pending = null;
    try {
      const raw = sessionStorage.getItem("rf_admin_pendingDoc");
      if (raw) {
        pending = JSON.parse(raw);
        sessionStorage.removeItem("rf_admin_pendingDoc");
      }
    } catch {}

    (async () => {
      try {
        const r = await fetch("/api/content", { credentials: "same-origin" });
        if (!alive) return;
        if (r.ok) {
          const j = await r.json();
          // Server doc is the baseline (so dirty check works); pending is
          // the working edit set we want to land.
          const baseline = normalizeContent(j);
          const working = pending ? normalizeContent(pending) : baseline;
          setDoc(working);
          setOriginal(JSON.stringify(baseline));
          return;
        }
        if (r.status === 404) {
          // Legitimate empty-KV state — first save will populate it.
          const baseline = normalizeContent(defaultContent);
          setDoc(pending ? normalizeContent(pending) : baseline);
          setOriginal(JSON.stringify(baseline));
          return;
        }
        // 5xx / unexpected status — DO NOT load the editor with defaults.
        // Saving over a transient KV outage would publish factory content.
        setLoadError(`/api/content returned ${r.status}`);
      } catch (e) {
        if (alive) setLoadError(e?.message || "network error");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const dirty = useMemo(
    () => doc && original && JSON.stringify(doc) !== original,
    [doc, original],
  );

  // Warn before tab close / navigation when there are unsaved changes.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  async function doLogout() {
    await fetch("/api/admin/logout", {
      method: "POST",
      credentials: "same-origin",
    });
    navigate("/admin/login", { replace: true });
  }

  function logout() {
    if (dirty) {
      setConfirmLogout(true);
      return;
    }
    doLogout();
  }

  async function save() {
    if (!doc || saving) return;
    setSaving(true);
    setStatus("saving…");
    try {
      const r = await fetch("/api/admin/content", {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(doc),
      });
      if (r.status === 401) {
        try {
          sessionStorage.setItem("rf_admin_pendingDoc", JSON.stringify(doc));
          sessionStorage.setItem("rf_admin_returnTo", location.pathname);
        } catch {}
        navigate("/admin/login");
        return;
      }
      if (!r.ok) {
        const txt = await r.text();
        setStatus(`error: ${r.status} ${txt}`);
        setSaving(false);
        return;
      }
      const data = await r.json();
      // Reflect the server-canonical updatedAt back into the working doc so
      // the footer's "last saved" timestamp is fresh without a reload.
      setDoc((prev) => (prev ? { ...prev, updatedAt: data.updatedAt } : prev));
      setStatus(`saved · ${new Date(data.updatedAt).toLocaleString()}`);
      setOriginal(JSON.stringify({ ...doc, updatedAt: data.updatedAt }));
    } catch (e) {
      setStatus(`error: ${e.message || "network"}`);
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0c0c0c",
          color: "#f4ece0",
          display: "grid",
          placeItems: "center",
          fontFamily: "system-ui, sans-serif",
          padding: "1.5rem",
        }}
      >
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <h1 style={{ fontSize: "1.05rem", fontWeight: 500, margin: 0, marginBottom: "0.7rem" }}>
            Couldn't load content
          </h1>
          <p style={{ color: "#aaa", fontSize: "0.9rem", margin: 0, marginBottom: "1rem" }}>
            {loadError}. Editor disabled to avoid overwriting the live document with defaults during a transient backend outage.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: "#f4ece0",
              color: "#0c0c0c",
              border: "none",
              padding: "0.5rem 1rem",
              fontSize: "0.85rem",
              borderRadius: 3,
              cursor: "pointer",
              fontWeight: 500,
              letterSpacing: "0.04em",
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (!doc) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0c0c0c",
          color: "#888",
          display: "grid",
          placeItems: "center",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        loading…
      </div>
    );
  }

  // Helpers to update nested fields immutably.
  const set = (path, value) => {
    setDoc((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let o = next;
      for (let i = 0; i < path.length - 1; i++) o = o[path[i]];
      o[path[path.length - 1]] = value;
      return next;
    });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0c0c0c",
        color: "#f4ece0",
        fontFamily: "system-ui, -apple-system, sans-serif",
        paddingBottom: "6rem",
      }}
    >
      <header
        style={{
          padding: "1.2rem 1.5rem",
          borderBottom: "1px solid #1a1a1a",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", gap: "1rem", alignItems: "baseline" }}>
          <Link
            to="/admin"
            style={{ color: "#888", textDecoration: "none", fontSize: "0.85rem" }}
          >
            ← admin
          </Link>
          <div style={{ fontSize: "1.05rem", letterSpacing: "0.04em" }}>
            Editor
          </div>
        </div>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <Link
            to="/admin/guide"
            style={{ color: "#888", textDecoration: "none", fontSize: "0.85rem" }}
          >
            guide
          </Link>
          <Link
            to="/admin/revisions"
            style={{ color: "#888", textDecoration: "none", fontSize: "0.85rem" }}
          >
            revisions
          </Link>
          <Link
            to="/"
            target="_blank"
            rel="noopener"
            style={{ color: "#888", textDecoration: "none", fontSize: "0.85rem" }}
          >
            view site ↗
          </Link>
          <button onClick={logout} style={ghostBtn}>
            sign out
          </button>
        </div>
      </header>

      <main
        style={{
          maxWidth: 880,
          margin: "0 auto",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "2rem",
        }}
      >
        <Section title="Home (hero)">
          <Field label="Title text">
            <input
              style={inputStyle}
              value={doc.hero.h1}
              onChange={(e) => set(["hero", "h1"], e.target.value)}
            />
          </Field>
          <Field label="Hero image">
            <DropZone
              slot="hero"
              value={doc.hero.image}
              onChange={(url) => set(["hero", "image"], url)}
            />
          </Field>
        </Section>

        <Section title="Works intro (/works)">
          <Field label="Reel video">
            <DropZone
              slot="reel"
              kind="video"
              value={doc.worksIntro.video}
              onChange={(url) => set(["worksIntro", "video"], url)}
            />
          </Field>
          <CtaEditor
            value={doc.worksIntro.cta}
            onChange={(next) => set(["worksIntro", "cta"], next)}
            labelHint="CTA label (was DEEPER)"
            targetHint="CTA target (path)"
          />
        </Section>

        <Section title="Works gallery backdrop (/works/deeper)">
          <Field label="Background image">
            <DropZone
              slot="backgrounds.works-deeper"
              value={doc.worksDeeper.background}
              onChange={(url) => set(["worksDeeper", "background"], url)}
            />
          </Field>
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#aaa", fontSize: "0.85rem" }}>
            <input
              type="checkbox"
              checked={doc.worksDeeper.filmBackdrop === true}
              onChange={(e) => set(["worksDeeper", "filmBackdrop"], e.target.checked)}
            />
            Show film grain & color flares overlay (applies to /works/deeper and /works/deeper/:slug)
          </label>
          <button
            type="button"
            onClick={() => setTileLayoutOpen(true)}
            style={{
              background: "transparent",
              color: "#aaa",
              border: "1px solid #2a2a2a",
              padding: "0.45rem 0.7rem",
              fontSize: "0.85rem",
              borderRadius: 3,
              cursor: "pointer",
              width: "fit-content",
            }}
          >
            Customize tile layout…
          </button>
        </Section>

        <Section title="Gallery pages — bottom margin">
          <Field label="Bottom margin (inches)" hint="Space below the last image on /works/deeper and every /works/deeper/:slug page. Default 0.5 in.">
            <input
              type="number"
              min="0"
              max="10"
              step="0.25"
              style={{ ...inputStyle, width: 120 }}
              value={doc.worksDeeper.galleryBottomMargin ?? 0.5}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                set(["worksDeeper", "galleryBottomMargin"], isNaN(v) ? 0.5 : Math.max(0, v));
              }}
            />
          </Field>
        </Section>

        <Section title="Contact">
          <Field label="Tagline">
            <textarea
              rows={2}
              style={textareaStyle}
              value={doc.contact.tagline}
              onChange={(e) => set(["contact", "tagline"], e.target.value)}
            />
          </Field>
          <Field label="Showreel label" hint="Bold link shown between the tagline and contact info — leave both fields blank to hide it">
            <input
              style={inputStyle}
              value={doc.contact.showreel?.label || ""}
              placeholder="e.g. Showreel"
              onChange={(e) => {
                const url = doc.contact.showreel?.url || "";
                const label = e.target.value;
                set(["contact", "showreel"], label || url ? { label, url } : null);
              }}
            />
          </Field>
          <Field label="Showreel URL">
            <input
              style={inputStyle}
              value={doc.contact.showreel?.url || ""}
              placeholder="https://..."
              onChange={(e) => {
                const label = doc.contact.showreel?.label || "";
                const url = e.target.value;
                set(["contact", "showreel"], label || url ? { label, url } : null);
              }}
            />
          </Field>
          <Field
            label="Email"
            hint={
              doc.contact.email && !EMAIL_RE.test(doc.contact.email)
                ? "Doesn't look like an email — the mailto: link may not work."
                : null
            }
          >
            <input
              type="email"
              style={inputStyle}
              value={doc.contact.email}
              onChange={(e) => set(["contact", "email"], e.target.value)}
            />
          </Field>
          <Field
            label="Phone"
            hint={
              doc.contact.phone && !/[0-9]/.test(doc.contact.phone)
                ? "No digits — the tel: link may not work."
                : null
            }
          >
            <input
              type="tel"
              style={inputStyle}
              value={doc.contact.phone}
              onChange={(e) => set(["contact", "phone"], e.target.value)}
            />
          </Field>
          <Field label="Background image">
            <DropZone
              slot="contact"
              value={doc.contact.image}
              onChange={(url) => set(["contact", "image"], url)}
            />
          </Field>
        </Section>

        <Section title="Works (8 entries)">
          {doc.works.map((w, idx) => (
            <WorkEditor
              key={w.slug}
              work={w}
              onChange={(next) => {
                setDoc((prev) => {
                  const copy = JSON.parse(JSON.stringify(prev));
                  copy.works[idx] = next;
                  return copy;
                });
              }}
            />
          ))}
        </Section>
      </main>

      <LayoutModal
        open={tileLayoutOpen}
        items={doc.works
          .filter((w) => w.enabled !== false)
          .map((w) => ({
            id: w.slug,
            url: w.image,
            label: w.title,
            x: w.x,
            y: w.y,
            w: w.w,
          }))}
        layoutFn={layoutWorksGallery}
        pageHeightVhFn={pageHeightVh}
        textTile={null}
        title="Layout — /works/deeper tiles"
        onChange={(nextItems) => {
          setDoc((prev) => {
            const copy = JSON.parse(JSON.stringify(prev));
            copy.works = copy.works.map((w) => {
              const it = nextItems.find((x) => x.id === w.slug);
              // Disabled works aren't in the modal — preserve their stored
              // x/y/w untouched. Only update enabled works whose tile was
              // present in the modal's items.
              if (!it) return w;
              const next = { ...w };
              if (typeof it.x === "number") next.x = it.x; else delete next.x;
              if (typeof it.y === "number") next.y = it.y; else delete next.y;
              if (typeof it.w === "number") next.w = it.w; else delete next.w;
              return next;
            });
            return copy;
          });
        }}
        onClose={() => setTileLayoutOpen(false)}
      />

      <footer
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "rgba(12,12,12,0.94)",
          borderTop: "1px solid #1a1a1a",
          padding: "0.9rem 1.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
          backdropFilter: "blur(8px)",
        }}
      >
        <div style={{ fontSize: "0.78rem", color: "#888" }}>
          v{doc.version || 1}
          {doc.updatedAt && ` · last saved ${new Date(doc.updatedAt).toLocaleString()}`}
          {status && (
            <span
              style={{
                marginLeft: "1rem",
                color: status.startsWith("error") ? "#e88" : "#aaa",
                fontWeight: status.startsWith("error") ? 500 : 400,
              }}
            >
              {status}
            </span>
          )}
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          style={{
            background: dirty && !saving ? "#f4ece0" : "#2a2a2a",
            color: dirty && !saving ? "#0c0c0c" : "#888",
            border: "none",
            padding: "0.6rem 1.2rem",
            fontSize: "0.9rem",
            borderRadius: 4,
            cursor: dirty && !saving ? "pointer" : "default",
            fontWeight: 500,
            letterSpacing: "0.04em",
          }}
        >
          {saving ? "saving…" : dirty ? "Save" : "Saved"}
        </button>
      </footer>
      <Modal
        open={confirmLogout}
        title="Sign out with unsaved changes?"
        confirmLabel="Sign out anyway"
        cancelLabel="Keep editing"
        destructive
        onCancel={() => setConfirmLogout(false)}
        onConfirm={() => {
          setConfirmLogout(false);
          doLogout();
        }}
      >
        You have unsaved edits. Signing out now will discard them — there's
        no autosave.
      </Modal>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section
      style={{
        background: "#0f0f0f",
        border: "1px solid #1a1a1a",
        borderRadius: 6,
        padding: "1.2rem",
      }}
    >
      <h2
        style={{
          margin: "0 0 1rem 0",
          fontSize: "0.95rem",
          fontWeight: 500,
          letterSpacing: "0.04em",
          color: "#f4ece0",
        }}
      >
        {title}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
        {children}
      </div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
      <span
        style={{
          color: "#888",
          fontSize: "0.72rem",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
        }}
      >
        {label}
      </span>
      {children}
      {hint && (
        <span style={{ color: "#d49b7c", fontSize: "0.72rem" }}>{hint}</span>
      )}
    </label>
  );
}

function WorkEditor({ work, onChange }) {
  const [open, setOpen] = useState(false);
  // Slot index counter — initialized to (current length + 1) so the first
  // append after load uses an index past the existing ones. Increments on
  // every append. Never decremented (deletions don't reuse indices,
  // matches R2's per-folder newest-wins semantics).
  const [nextSlotIndex, setNextSlotIndex] = useState(work.gallery.length + 1);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const setField = (k, v) => onChange({ ...work, [k]: v });
  const setGalleryUrl = (i, url) => {
    const g = work.gallery.map((item, idx) => (idx === i ? { ...item, url } : item));
    onChange({ ...work, gallery: g });
  };
  const appendGallery = () => {
    onChange({ ...work, gallery: [...work.gallery, { url: "" }] });
    setNextSlotIndex((n) => n + 1);
  };
  const removeGallery = (i) => {
    if (work.gallery.length <= 1) return;
    const g = work.gallery.filter((_, idx) => idx !== i);
    onChange({ ...work, gallery: g });
  };
  const slotIndexForItem = (i) => {
    if (work.gallery[i] && work.gallery[i].url) return i + 1;
    return nextSlotIndex - (work.gallery.length - 1 - i);
  };
  return (
    <div
      style={{
        border: "1px solid #1a1a1a",
        borderRadius: 4,
        padding: "0.8rem 1rem",
        background: "#0c0c0c",
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          ...ghostBtn,
          width: "100%",
          textAlign: "left",
          padding: 0,
          fontSize: "0.92rem",
        }}
      >
        <span
          style={{
            color: work.enabled === false ? "#666" : "#f4ece0",
            fontWeight: 500,
            textDecoration: work.enabled === false ? "line-through" : "none",
          }}
        >
          {work.title}
        </span>
        <span style={{ color: "#666", marginLeft: "0.6rem" }}>· {work.slug}</span>
        {work.enabled === false && (
          <span style={{ color: "#888", marginLeft: "0.6rem", fontSize: "0.75rem" }}>(hidden)</span>
        )}
        <span style={{ color: "#444", float: "right" }}>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div
          style={{
            marginTop: "0.9rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.7rem",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#aaa", fontSize: "0.85rem" }}>
            <input
              type="checkbox"
              checked={work.enabled !== false}
              onChange={(e) => setField("enabled", e.target.checked)}
            />
            Visible on /works/deeper
          </label>
          <Field label="Title">
            <input
              style={inputStyle}
              value={work.title}
              onChange={(e) => setField("title", e.target.value)}
            />
          </Field>
          <div style={{ display: "flex", gap: "0.7rem" }}>
            <Field label="Year">
              <input
                style={inputStyle}
                value={work.year}
                onChange={(e) => setField("year", e.target.value)}
              />
            </Field>
            <Field label="Role">
              <input
                style={inputStyle}
                value={work.role}
                onChange={(e) => setField("role", e.target.value)}
              />
            </Field>
          </div>
          <Field label="Description">
            <textarea
              rows={4}
              style={textareaStyle}
              value={work.description}
              onChange={(e) => setField("description", e.target.value)}
            />
          </Field>
          <Field label="External link label" hint="Shown as the large link at the bottom of this work's page — leave both fields blank to hide it">
            <input
              style={inputStyle}
              value={work.externalLink?.label || ""}
              placeholder="e.g. Watch on IMDb"
              onChange={(e) => {
                const url = work.externalLink?.url || "";
                const label = e.target.value;
                setField("externalLink", label || url ? { label, url } : null);
              }}
            />
          </Field>
          <Field label="External link URL">
            <input
              style={inputStyle}
              value={work.externalLink?.url || ""}
              placeholder="https://..."
              onChange={(e) => {
                const label = work.externalLink?.label || "";
                const url = e.target.value;
                setField("externalLink", label || url ? { label, url } : null);
              }}
            />
          </Field>
          <Field label="Cover image">
            <DropZone
              slot={`works.${work.slug}.cover`}
              value={work.image}
              onChange={(url) => setField("image", url)}
            />
          </Field>
          <Field label={`Gallery (${work.gallery.length} stills, top → bottom on detail page)`}>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {work.gallery.map((g, i) => (
                <div key={i} style={{ display: "flex", gap: "0.4rem", alignItems: "stretch" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <DropZone
                      slot={`works.${work.slug}.gallery.${slotIndexForItem(i)}`}
                      value={g.url}
                      onChange={(url) => setGalleryUrl(i, url)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeGallery(i)}
                    disabled={work.gallery.length <= 1}
                    title={work.gallery.length <= 1 ? "Gallery requires at least 1 image" : "Remove this slot"}
                    style={{
                      background: "transparent",
                      color: work.gallery.length <= 1 ? "#444" : "#aaa",
                      border: "1px solid #2a2a2a",
                      padding: "0 0.7rem",
                      fontSize: "0.85rem",
                      borderRadius: 3,
                      cursor: work.gallery.length <= 1 ? "default" : "pointer",
                    }}
                  >
                    remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={appendGallery}
                style={{
                  background: "transparent",
                  color: "#aaa",
                  border: "1px dashed #3a3a3a",
                  padding: "0.5rem",
                  fontSize: "0.85rem",
                  borderRadius: 3,
                  cursor: "pointer",
                  marginTop: "0.3rem",
                }}
              >
                + add image
              </button>
              <button
                type="button"
                onClick={() => setLayoutOpen(true)}
                style={{
                  background: "transparent",
                  color: "#aaa",
                  border: "1px solid #2a2a2a",
                  padding: "0.45rem 0.7rem",
                  fontSize: "0.85rem",
                  borderRadius: 3,
                  cursor: "pointer",
                  marginTop: "0.3rem",
                  width: "fit-content",
                }}
              >
                Customize layout…
              </button>
            </div>
          </Field>
          <LayoutModal
            open={layoutOpen}
            items={work.gallery.map((g, i) => ({
              id: String(i),
              url: g.url,
              label: shortenUrl(g.url) || `Image ${i + 1}`,
              x: g.x,
              y: g.y,
              w: g.w,
            }))}
            layoutFn={layoutWorkDetail}
            pageHeightVhFn={(count, vwToVh) => workDetailPageHeightVh(count, work.descriptionPosition?.y ?? 112, [], vwToVh)}
            textTile={{
              x: typeof work.descriptionPosition?.x === "number" ? work.descriptionPosition.x : 25,
              y: typeof work.descriptionPosition?.y === "number" ? work.descriptionPosition.y : 112,
              w: typeof work.descriptionPosition?.w === "number" ? work.descriptionPosition.w : 50,
              label: `${work.title || "Description"}${work.year ? " · " + work.year : ""}${work.role ? " · " + work.role : ""}`,
            }}
            title={`Layout — ${work.title} gallery`}
            onChange={(updatedItems, updatedText) => {
              const nextGallery = updatedItems.map((item) => {
                const orig = work.gallery[parseInt(item.id, 10)] || {};
                const next = { ...orig, url: orig.url };
                if (typeof item.x === "number") next.x = item.x; else delete next.x;
                if (typeof item.y === "number") next.y = item.y; else delete next.y;
                if (typeof item.w === "number") next.w = item.w; else delete next.w;
                return next;
              });
              const nextWork = { ...work, gallery: nextGallery };
              if (updatedText) {
                nextWork.descriptionPosition = {
                  x: updatedText.x,
                  y: updatedText.y,
                  w: updatedText.w,
                };
              }
              onChange(nextWork);
            }}
            onClose={() => setLayoutOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

function CtaEditor({ value, onChange, labelHint, targetHint }) {
  return (
    <div style={{ display: "flex", gap: "0.7rem" }}>
      <Field label={labelHint || "CTA label"}>
        <input
          style={inputStyle}
          value={value.label}
          onChange={(e) => onChange({ ...value, label: e.target.value })}
        />
      </Field>
      <Field label={targetHint || "CTA target"}>
        <input
          style={inputStyle}
          value={value.target}
          onChange={(e) => onChange({ ...value, target: e.target.value })}
        />
      </Field>
    </div>
  );
}

function shortenUrl(url) {
  if (!url) return "";
  try {
    const noQuery = url.split("?")[0];
    const parts = noQuery.split("/");
    return parts[parts.length - 1] || noQuery;
  } catch {
    return url;
  }
}

const inputStyle = {
  background: "#161616",
  border: "1px solid #2a2a2a",
  color: "#f4ece0",
  padding: "0.55rem 0.7rem",
  fontSize: "0.95rem",
  borderRadius: 3,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const textareaStyle = {
  ...inputStyle,
  resize: "vertical",
  fontFamily: "inherit",
  lineHeight: 1.5,
};

const ghostBtn = {
  background: "transparent",
  color: "#aaa",
  border: "none",
  padding: "0.3rem 0.5rem",
  fontSize: "0.85rem",
  cursor: "pointer",
};
